<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted, nextTick } from "vue";
import {
    Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions,
    Dialog,
    DialogPanel,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { useRecipeCategoryCollectionStore } from "~/stores/recipe/recipeCategoryCollection";
import { ChevronUpDownIcon, FunnelIcon, TrashIcon, CheckIcon } from "@heroicons/vue/24/solid";
import { useFilterRecipeStore } from "~/stores/filter/useFilterRecipeStore";
import { useRoute } from "vue-router";
import { navigateTo } from "#app";

const props = defineProps<{
    selectedCategory?: string,
    basePath: string,
}>();

const route = useRoute();
const recipeCategoryCollectionStore = useRecipeCategoryCollectionStore();
const filterRecipeStore = useFilterRecipeStore();

useAsyncData('filterAsideRecipeCategories', async () => recipeCategoryCollectionStore.fetchList());
useAsyncData('ingredientsFilterRecipeStore', async () => filterRecipeStore.fetchIngredients(props.selectedCategory));
useAsyncData('cuisinesFilterRecipeStore', async () => filterRecipeStore.fetchCuisines(props.selectedCategory));

const selectedCategory = props.selectedCategory ? props.selectedCategory : 'all';
const subCategories = computed(() => recipeCategoryCollectionStore.getFilter);

const filterIngredients = computed(() => filterRecipeStore.getFilterIngredients);
const selectedFilterIngredientsData = computed(() => filterRecipeStore.getSelectedFilterIngredients);
const selectedFilterIngredients = ref([]);
const searchFilterIngredients = ref('');

const filterCuisines = computed(() => filterRecipeStore.getFilterCuisines);
const selectedFilterCuisinesData = computed(() => filterRecipeStore.getSelectedFilterCuisines);
const selectedFilterCuisines = ref([]);
const searchFilterCuisines = ref('');

const mobileFiltersOpen = ref(false);
const skeletonSizes = [30, 40, 50, 60, 70, 80];

if (route?.query?.ingredients) {
    selectedFilterIngredients.value = route.query.ingredients.split(',').map(value => Number(value));
    filterRecipeStore.fetchSelectedIngredients(selectedFilterIngredients.value);
}
if (route?.query?.cuisines) {
    selectedFilterCuisines.value = route.query.cuisines.split(',').map(value => Number(value));
    filterRecipeStore.fetchSelectedCuisines(selectedFilterCuisines.value);
}

const changeFilter = () => {
    filterRecipeStore.fetchSelectedIngredients(selectedFilterIngredients.value);
    filterRecipeStore.fetchSelectedCuisines(selectedFilterCuisines.value);

    const query = {};
    if (selectedFilterIngredients.value && selectedFilterIngredients.value.length) {
        query.ingredients = selectedFilterIngredients.value.join(',');
    }

    if (selectedFilterCuisines.value && selectedFilterCuisines.value.length) {
        query.cuisines = selectedFilterCuisines.value.join(',');
    }

    if (Object.keys(query).length) {
        useRouter().replace({
            path: props.basePath,
            query,
        });
    } else {
        navigateTo(props.basePath);
    }
}

const removeSelectedFilterIngredient = function (id) {
    _remove(selectedFilterIngredients.value, (value) => value === id);
    changeFilter();
}

const removeSelectedFilterCuisine = function (id) {
    _remove(selectedFilterCuisines.value, (value) => value === id);
    changeFilter();
}

const isFiltered = () => {
    if (
        (selectedFilterIngredients.value && selectedFilterIngredients.value.length) ||
        (selectedFilterCuisines.value && selectedFilterCuisines.value.length)
    ) {
        return true;
    }

    return false;
}

const clearFilter = () => {
    selectedFilterIngredients.value = [];
    selectedFilterCuisines.value = [];
    mobileFiltersOpen.value = false;
}

nextTick(() => {
    filterRecipeStore.fetchSelectedIngredients(selectedFilterIngredients.value);
    filterRecipeStore.fetchSelectedCuisines(selectedFilterCuisines.value);

    watch(selectedFilterIngredients, () => {
        changeFilter();
    });

    watch(selectedFilterCuisines, () => {
        changeFilter();
    });

    watch(searchFilterIngredients, () => {
        filterRecipeStore.fetchIngredients(props.selectedCategory, searchFilterIngredients.value);
    });

    watch(searchFilterCuisines, () => {
        filterRecipeStore.fetchCuisines(props.selectedCategory, searchFilterCuisines.value);
    });
});
</script>

<template>
    <TransitionRoot as="template" :show="mobileFiltersOpen">
        <Dialog as="div" class="relative z-40 lg:hidden" @close="mobileFiltersOpen = false">
            <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0"
                             enter-to="opacity-100" leave="transition-opacity ease-linear duration-300"
                             leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-black bg-opacity-25"/>
            </TransitionChild>

            <div class="fixed inset-0 z-40 flex">
                <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                                 enter-from="translate-x-full" enter-to="translate-x-0"
                                 leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                                 leave-to="translate-x-full">
                    <DialogPanel
                        class="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 space-y-4">
                        <div class="flex items-center justify-between px-4">
                            <h2 class="text-lg font-medium">Filters</h2>
                            <button type="button"
                                    class="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2"
                                    @click="mobileFiltersOpen = false">
                                <span class="sr-only">Close menu</span>
                                <XMarkIcon class="h-6 w-6" aria-hidden="true"/>
                            </button>
                        </div>
                        <form class="border-t border-gray-200 px-4">
                            <ul role="list" class="py-4 space-y-2">
                                <li v-for="category in subCategories" :key="category.name">
                                    <nuxt-link
                                        :to="category.href"
                                        class="hover:underline underline-offset-4"
                                        :class="{'underline': selectedCategory === category.slug}"
                                    >{{ category.name }}
                                    </nuxt-link>
                                </li>
                            </ul>
                            <div v-if="filterRecipeStore.cuisines !== undefined && filterRecipeStore.ingredients !== undefined" class="space-y-6">
                                <Combobox v-model="selectedFilterIngredients" multiple>
                                    <div class="relative mt-1">
                                        <div
                                            class="relative w-full cursor-default rounded-lg text-left border-2 border-indigo-500 sm:text-sm"
                                        >
                                            <ul v-if="selectedFilterIngredientsData.length" class="flex flex-wrap gap-1 p-3">
                                                <li v-for="item in selectedFilterIngredientsData" :key="item.value">
                                                    <button
                                                        class="flex items-center text-left rounded-md bg-gray-100 hover:bg-gray-200 px-2 py-1 gap-1"
                                                        @click.prevent="removeSelectedFilterIngredient(item.value)"
                                                    >
                                                        {{ item.label }}
                                                        <span>
                                        <XMarkIcon class="h-4 w-4" aria-hidden="true"/>
                                    </span>
                                                    </button>
                                                </li>
                                            </ul>
                                            <ComboboxInput
                                                class="w-full border-none rounded-lg py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                                                placeholder="Start type the ingredient"
                                                @change="searchFilterIngredients = $event.target.value"
                                            />
                                            <ComboboxButton
                                                class="absolute right-0 bottom-2 flex items-center pr-2"
                                            >
                                                <ChevronUpDownIcon
                                                    class="h-5 w-5 text-indigo-800"
                                                    aria-hidden="true"
                                                />
                                            </ComboboxButton>
                                        </div>
                                        <TransitionRoot
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                            @after-leave="searchFilterIngredients = ''"
                                        >
                                            <ComboboxOptions
                                                class="absolute mt-1 z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                                            >
                                                <div
                                                    v-if="filterIngredients.length === 0 && searchFilterIngredients !== ''"
                                                    class="relative cursor-default select-none py-2 px-4 text-gray-700"
                                                >
                                                    Nothing found.
                                                </div>

                                                <ComboboxOption
                                                    v-for="item in filterIngredients"
                                                    as="template"
                                                    :key="item.value"
                                                    :value="item.value"
                                                    v-slot="{ selected, active }"
                                                >
                                                    <li
                                                        class="relative cursor-default select-none py-2 pl-10 pr-4"
                                                        :class="{
                  'bg-indigo-800 text-white': active,
                  'text-gray-900': !active,
                }"
                                                    >
                                    <span
                                        class="block truncate"
                                        :class="{ 'font-medium': selected, 'font-normal': !selected }"
                                    >
                                      {{ item.label }}
                                    </span>
                                                        <span
                                                            v-if="selected"
                                                            class="absolute inset-y-0 left-0 flex items-center pl-3"
                                                            :class="{ 'text-white': active, 'text-indigo-800': !active }"
                                                        >
                                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                    </span>
                                                    </li>
                                                </ComboboxOption>
                                            </ComboboxOptions>
                                        </TransitionRoot>
                                    </div>
                                </Combobox>
                                <Combobox v-model="selectedFilterCuisines" multiple>
                                    <div class="relative mt-1">
                                        <div
                                            class="relative w-full cursor-default rounded-lg text-left border-2 border-indigo-500 sm:text-sm"
                                        >
                                            <ul v-if="selectedFilterCuisinesData.length" class="flex flex-wrap gap-1 p-3">
                                                <li v-for="item in selectedFilterCuisinesData" :key="item.value">
                                                    <button
                                                        class="flex items-center text-left rounded-md bg-gray-100 hover:bg-gray-200 px-2 py-1 gap-1"
                                                        @click.prevent="removeSelectedFilterCuisine(item.value)"
                                                    >
                                                        {{ item.label }}
                                                        <span>
                                        <XMarkIcon class="h-4 w-4" aria-hidden="true"/>
                                    </span>
                                                    </button>
                                                </li>
                                            </ul>
                                            <ComboboxInput
                                                class="w-full border-none rounded-lg py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                                                placeholder="Start type the cuisine"
                                                @change="searchFilterCuisines = $event.target.value"
                                            />
                                            <ComboboxButton
                                                class="absolute right-0 bottom-2 flex items-center pr-2"
                                            >
                                                <ChevronUpDownIcon
                                                    class="h-5 w-5 text-indigo-800"
                                                    aria-hidden="true"
                                                />
                                            </ComboboxButton>
                                        </div>
                                        <TransitionRoot
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                            @after-leave="searchFilterCuisines = ''"
                                        >
                                            <ComboboxOptions
                                                class="absolute mt-1 z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                                            >
                                                <div
                                                    v-if="filterCuisines.length === 0 && searchFilterCuisines !== ''"
                                                    class="relative cursor-default select-none py-2 px-4 text-gray-700"
                                                >
                                                    Nothing found.
                                                </div>

                                                <ComboboxOption
                                                    v-for="item in filterCuisines"
                                                    as="template"
                                                    :key="item.value"
                                                    :value="item.value"
                                                    v-slot="{ selected, active }"
                                                >
                                                    <li
                                                        class="relative cursor-default select-none py-2 pl-10 pr-4"
                                                        :class="{
                  'bg-indigo-800 text-white': active,
                  'text-gray-900': !active,
                }"
                                                    >
                                    <span
                                        class="block truncate"
                                        :class="{ 'font-medium': selected, 'font-normal': !selected }"
                                    >
                                      {{ item.label }}
                                    </span>
                                                        <span
                                                            v-if="selected"
                                                            class="absolute inset-y-0 left-0 flex items-center pl-3"
                                                            :class="{ 'text-white': active, 'text-indigo-800': !active }"
                                                        >
                                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                    </span>
                                                    </li>
                                                </ComboboxOption>
                                            </ComboboxOptions>
                                        </TransitionRoot>
                                    </div>
                                </Combobox>
                            </div>
                        </form>
                        <div class="mx-4 grid grid-cols-2 gap-2">
                            <div>
                                <button v-if="isFiltered()"
                                        type="button"
                                        class="mt-4 bg-white w-full justify-center rounded-md border-indigo-500 border-2 px-4 py-2 font-medium text-indigo-800 focus:outline-none flex gap-1 items-center"
                                        @click="clearFilter()"
                                >
                                    <TrashIcon class="w-4 h-4" />
                                    <span>Clear</span>
                                </button>
                            </div>
                            <div>
                                <button type="button"
                                        class="mt-4 bg-white w-full justify-center rounded-md border-indigo-500 border-2 px-4 py-2 font-medium text-indigo-800 focus:outline-none flex gap-1 items-center"
                                        @click="mobileFiltersOpen = false"
                                >
                                    <span>Apply</span>
                                </button>
                            </div>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>

    <button type="button"
            class="fixed z-10 bg-white bottom-4 left-4 right-4 justify-center rounded-md border-indigo-500 border-2 px-4 py-2 font-medium text-indigo-800 focus:outline-none lg:hidden flex gap-2 items-center"
            @click="mobileFiltersOpen = true">
        <FunnelIcon class="h-4 w-4" aria-hidden="true"/>
        <span>Filters</span>
    </button>
    <form class="hidden lg:block space-y-6">
        <ul v-if="recipeCategoryCollectionStore.list !== undefined" role="list" class="space-y-2">
            <li v-for="category in subCategories" :key="category.name">
                <nuxt-link
                    :to="category.href"
                    class="hover:underline underline-offset-4"
                    :class="{'underline': selectedCategory === category.slug}"
                >{{ category.name }}</nuxt-link>
            </li>
        </ul>
        <ul v-else role="list" class="space-y-2">
            <li v-for="idx in 10" :key="idx">
                <span :class="`block animate-pulse h-6 w-[${skeletonSizes[_random(0, 5, false)]}%] rounded bg-gray-100`"></span>
            </li>
        </ul>

        <div v-if="filterRecipeStore.cuisines !== undefined && filterRecipeStore.ingredients !== undefined" class="space-y-6">
            <Combobox v-model="selectedFilterIngredients" multiple>
                <div class="relative mt-1">
                    <div
                        class="relative w-full cursor-default rounded-lg text-left border-2 border-indigo-500 sm:text-sm"
                    >
                        <ul v-if="selectedFilterIngredientsData.length" class="flex flex-wrap gap-1 p-3">
                            <li v-for="item in selectedFilterIngredientsData" :key="item.value">
                                <button
                                    class="flex items-center text-left rounded-md bg-gray-100 hover:bg-gray-200 px-2 py-1 gap-1"
                                    @click.prevent="removeSelectedFilterIngredient(item.value)"
                                >
                                    {{ item.label }}
                                    <span>
                                        <XMarkIcon class="h-4 w-4" aria-hidden="true"/>
                                    </span>
                                </button>
                            </li>
                        </ul>
                        <ComboboxInput
                            class="w-full border-none rounded-lg py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                            placeholder="Start type the ingredient"
                            @change="searchFilterIngredients = $event.target.value"
                        />
                        <ComboboxButton
                            class="absolute right-0 bottom-2 flex items-center pr-2"
                        >
                            <ChevronUpDownIcon
                                class="h-5 w-5 text-indigo-800"
                                aria-hidden="true"
                            />
                        </ComboboxButton>
                    </div>
                    <TransitionRoot
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        @after-leave="searchFilterIngredients = ''"
                    >
                        <ComboboxOptions
                            class="absolute mt-1 z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                        >
                            <div
                                v-if="filterIngredients.length === 0 && searchFilterIngredients !== ''"
                                class="relative cursor-default select-none py-2 px-4 text-gray-700"
                            >
                                Nothing found.
                            </div>

                            <ComboboxOption
                                v-for="item in filterIngredients"
                                as="template"
                                :key="item.value"
                                :value="item.value"
                                v-slot="{ selected, active }"
                            >
                                <li
                                    class="relative cursor-default select-none py-2 pl-10 pr-4"
                                    :class="{
                  'bg-indigo-800 text-white': active,
                  'text-gray-900': !active,
                }"
                                >
                                    <span
                                        class="block truncate"
                                        :class="{ 'font-medium': selected, 'font-normal': !selected }"
                                    >
                                      {{ item.label }}
                                    </span>
                                    <span
                                        v-if="selected"
                                        class="absolute inset-y-0 left-0 flex items-center pl-3"
                                        :class="{ 'text-white': active, 'text-indigo-800': !active }"
                                    >
                                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </li>
                            </ComboboxOption>
                        </ComboboxOptions>
                    </TransitionRoot>
                </div>
            </Combobox>
            <Combobox v-model="selectedFilterCuisines" multiple>
                <div class="relative mt-1">
                    <div
                        class="relative w-full cursor-default rounded-lg text-left border-2 border-indigo-500 sm:text-sm"
                    >
                        <ul v-if="selectedFilterCuisinesData.length" class="flex flex-wrap gap-1 p-3">
                            <li v-for="item in selectedFilterCuisinesData" :key="item.value">
                                <button
                                    class="flex items-center text-left rounded-md bg-gray-100 hover:bg-gray-200 px-2 py-1 gap-1"
                                    @click.prevent="removeSelectedFilterCuisine(item.value)"
                                >
                                    {{ item.label }}
                                    <span>
                                        <XMarkIcon class="h-4 w-4" aria-hidden="true"/>
                                    </span>
                                </button>
                            </li>
                        </ul>
                        <ComboboxInput
                            class="w-full border-none rounded-lg py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                            placeholder="Start type the cuisine"
                            @change="searchFilterCuisines = $event.target.value"
                        />
                        <ComboboxButton
                            class="absolute right-0 bottom-2 flex items-center pr-2"
                        >
                            <ChevronUpDownIcon
                                class="h-5 w-5 text-indigo-800"
                                aria-hidden="true"
                            />
                        </ComboboxButton>
                    </div>
                    <TransitionRoot
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        @after-leave="searchFilterCuisines = ''"
                    >
                        <ComboboxOptions
                            class="absolute mt-1 z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                        >
                            <div
                                v-if="filterCuisines.length === 0 && searchFilterCuisines !== ''"
                                class="relative cursor-default select-none py-2 px-4 text-gray-700"
                            >
                                Nothing found.
                            </div>

                            <ComboboxOption
                                v-for="item in filterCuisines"
                                as="template"
                                :key="item.value"
                                :value="item.value"
                                v-slot="{ selected, active }"
                            >
                                <li
                                    class="relative cursor-default select-none py-2 pl-10 pr-4"
                                    :class="{
                  'bg-indigo-800 text-white': active,
                  'text-gray-900': !active,
                }"
                                >
                                    <span
                                        class="block truncate"
                                        :class="{ 'font-medium': selected, 'font-normal': !selected }"
                                    >
                                      {{ item.label }}
                                    </span>
                                    <span
                                        v-if="selected"
                                        class="absolute inset-y-0 left-0 flex items-center pl-3"
                                        :class="{ 'text-white': active, 'text-indigo-800': !active }"
                                    >
                                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </li>
                            </ComboboxOption>
                        </ComboboxOptions>
                    </TransitionRoot>
                </div>
            </Combobox>
        </div>

        <div v-if="isFiltered()">
            <button type="button"
                    class="bg-indigo-800 hover:bg-indigo-500 duration-300 w-full justify-center rounded-md px-4 py-2 font-medium text-white focus:outline-none flex gap-1 items-center"
                    @click="clearFilter()"
            >
                <TrashIcon class="w-4 h-4" />
                <span>Clear</span>
            </button>
        </div>
    </form>
</template>